// Prime pieces at the counter (12 §12.5, "Prime pieces"): documents and parcels have no pre-printed
// barcode, so the counter enters a weight, dimensions and a description for each piece, and each
// gets the label `{trackingNo}-P1`, `-P2`, … from the tracking number. Prime is priced by weight
// from the rate card, by the same `quote` function as every other service.
import { MAX_PIECES_PER_SHIPMENT, pieceId } from '@ecobox/domain';
import type { InvoiceBox, InvoiceLineView } from './invoice';

/** A size code no rate row names: only a rule for "any size" prices a Prime piece. */
export const PRIME_SIZE = 'PRIME';
/** The heaviest piece one command accepts. */
export const MAX_PIECE_KG = 500;

export interface PieceDraft {
  /** Stable while the row exists, so editing does not move focus. */
  key: number;
  description: string;
  weightKg: number | undefined;
  dims?: { l?: number | undefined; w?: number | undefined; h?: number | undefined } | undefined;
}

export function emptyPiece(key: number): PieceDraft {
  return { key, description: '', weightKg: undefined };
}

/** The piece numbered `n` (1-based) is priced under the key `P{n}` until the tracking number exists. */
export const pieceKey = (n: number): string => `P${n}`;

/** What is wrong with the pieces, in words a cashier understands. Empty means they are complete. */
export function pieceProblems(pieces: readonly PieceDraft[]): string[] {
  const out: string[] = [];
  if (pieces.length === 0) out.push('Add at least one piece, with its weight.');
  if (pieces.length > MAX_PIECES_PER_SHIPMENT) {
    out.push(`A Prime shipment carries at most ${MAX_PIECES_PER_SHIPMENT} pieces.`);
  }
  pieces.forEach((p, i) => {
    const n = i + 1;
    if (p.description.trim() === '') out.push(`Piece ${n}: say what it is (document, parcel…).`);
    if (p.weightKg === undefined || p.weightKg <= 0) {
      out.push(`Piece ${n}: enter the weight (Prime is priced by weight).`);
    } else if (p.weightKg > MAX_PIECE_KG) {
      out.push(`Piece ${n}: ${p.weightKg} kg is over the ${MAX_PIECE_KG} kg limit.`);
    }
    const d = p.dims;
    const some = d && (d.l || d.w || d.h);
    if (some && !(d.l && d.w && d.h)) {
      out.push(`Piece ${n}: enter length, width and height, or leave all three empty.`);
    }
  });
  return out;
}

/** The pieces as the boxes the pricing function wants: a key, a size no rule names, a weight. */
export function piecesAsInvoiceBoxes(pieces: readonly PieceDraft[]): InvoiceBox[] {
  return pieces.map((p, i) => ({
    barcode: pieceKey(i + 1),
    sizeId: PRIME_SIZE,
    weightKg: p.weightKg,
    dims: p.dims,
    sold: false,
  }));
}

/** The labels the pieces will get once the shipment has its tracking number. */
export function pieceLabels(trackingNo: string, count: number): string[] {
  return Array.from({ length: count }, (_, i) => pieceId(trackingNo, i + 1));
}

/** The `pieces` of the `collectCargo` payload (04 §4.2). The server makes the labels. */
export function piecesPayload(pieces: readonly PieceDraft[]): {
  weightKg: number;
  dimsCm?: { l: number; w: number; h: number };
  description: string;
}[] {
  return pieces.map((p) => {
    const d = p.dims;
    return {
      weightKg: p.weightKg ?? 0,
      ...(d?.l && d.w && d.h ? { dimsCm: { l: d.l, w: d.w, h: d.h } } : {}),
      description: p.description.trim(),
    };
  });
}

/** Freight lines of a Prime quote say "Prime piece 2 (1.5 kg)", not "Box PRIME to …". */
export function primeLineLabels(
  lines: readonly InvoiceLineView[],
  pieces: readonly PieceDraft[],
): InvoiceLineView[] {
  return lines.map((l) => {
    const match = l.boxKey ? /^P(\d+)$/.exec(l.boxKey) : null;
    if (!match) return l;
    const n = Number(match[1]);
    const kg = pieces[n - 1]?.weightKg;
    return {
      ...l,
      label: `Prime piece ${n}${kg !== undefined ? ` (${kg} kg)` : ''}`,
      ...(l.basis ? { basis: l.basis.replace(/size PRIME box/g, 'Prime piece') } : {}),
    };
  });
}

/** The quote's failure reasons name the size; for Prime there is none. */
export function primeReason(reason: string): string {
  return reason.replace(/size PRIME/g, 'a Prime piece (priced by weight)');
}
